import React from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  ButtonGroup,
  Col,
  Form,
  FormLabel,
  Row,
  ToggleButton,
} from 'react-bootstrap'
import Input from '../../../../../../components/form/Input'
import { Node } from 'reactflow'
import { BaseNodeData } from '../../base/types'
import { NodeTypes } from '../../../../../../shared/models/rule-node'
import _ from 'lodash'
import CreatableSelect from 'react-select/creatable'
import Flex from '../../../../../../components/common/Flex'
import { AggregationType } from '../../../../../../shared/models/time.model'

interface DurationWithUnit {
  duration: number
  unit: string
}

export interface GetOriginTelemetryConfiguration {
  name: string
  isDebugMode: boolean
  keys: string[]
  intervalType: 'DYNAMIC' | 'STATIC'
  dynamicInterval?: {
    intervalStartPattern: string
    intervalEndPattern: string
  }
  staticInterval: {
    intervalDurationStart: DurationWithUnit
    intervalDurationEnd: DurationWithUnit
  }
  aggregation?: AggregationType
}

interface GetOriginTelemetryFormProps {
  onSubmit: (
    data: Node<
      BaseNodeData<GetOriginTelemetryConfiguration>,
      NodeTypes.ENRICHMENT_ORIGIN_TELEMETRY
    >
  ) => void
  defaultValues: Node<
    BaseNodeData<GetOriginTelemetryConfiguration>,
    NodeTypes.ENRICHMENT_ORIGIN_TELEMETRY
  >
}

const GetOriginTelemetryForm = ({
  onSubmit,
  defaultValues,
}: GetOriginTelemetryFormProps) => {
  const form = useForm<
    Node<
      BaseNodeData<GetOriginTelemetryConfiguration>,
      NodeTypes.ENRICHMENT_ORIGIN_TELEMETRY
    >
  >({
    defaultValues,
  })
  const {
    handleSubmit,
    reset,
    register,
    watch,
    formState: { errors },
  } = form
  const { configuration } = watch('data')
  return (
    <FormProvider {...form}>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} id={'form_rule_node'}>
        <Row className={''}>
          <Form.Group as={Col} lg={6} md={12}>
            <Input
              register={register('data.name', {
                required: 'Le nom est obligatoire',
              })}
              label={'Nom'}
              error={errors.data?.name ? errors.data?.name : undefined}
            />
          </Form.Group>
          <Form.Group as={Col} lg={6} md={12}>
            <Form.Check
              type="switch"
              label="Debug Mode ?"
              {...register('data.isDebugMode')}
            />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} lg={12}>
            <FormLabel>Clés</FormLabel>
            <Controller
              name={'data.configuration.keys'}
              control={form.control}
              render={({ field: { onChange, value: values } }) => {
                return (
                  <CreatableSelect
                    classNamePrefix={'react-select'}
                    isClearable
                    isMulti
                    value={values?.map((value: string) => ({
                      value,
                      label: value,
                    }))}
                    onChange={(values, actionMeta) => {
                      const strValues = _.chain(values)
                        .map('value')
                        .uniq()
                        .value()
                      onChange(strValues)
                    }}
                  />
                )
              }}
            />
          </Form.Group>
        </Row>
        <Row className={'mt-3'}>
          <Form.Group as={Col} lg={12}>
            <Flex
              direction={'row'}
              alignItems={'center'}
              justifyContent={'between'}
            >
              <FormLabel>Type d'intervale :</FormLabel>
              <ButtonGroup>
                <ToggleButton
                  value={'data'}
                  type="radio"
                  variant={'outline-primary'}
                  onClick={() => {
                    form.setValue('data.configuration.intervalType', 'DYNAMIC')
                  }}
                  checked={configuration?.intervalType === 'DYNAMIC'}
                  {...register('data.configuration.intervalType')}
                >
                  Dynamique
                </ToggleButton>
                <ToggleButton
                  value={'metadata'}
                  type="radio"
                  variant={'outline-primary'}
                  onClick={() => {
                    form.setValue('data.configuration.intervalType', 'STATIC')
                  }}
                  checked={configuration?.intervalType === 'STATIC'}
                  {...register('data.configuration.intervalType')}
                >
                  Statique
                </ToggleButton>
              </ButtonGroup>
            </Flex>
          </Form.Group>
        </Row>
        {configuration?.intervalType === 'STATIC' && (
          <div>
            <Row>
              <Form.Group as={Col} lg={3}>
                <Input
                  type={'numeric'}
                  register={register(
                    'data.configuration.staticInterval.intervalDurationStart.duration',
                    {
                      required: 'La duréé de début est obligatoire',
                      valueAsNumber: true,
                      shouldUnregister: true,
                    }
                  )}
                  label={'Interval de début'}
                  error={
                    errors.data?.configuration?.staticInterval
                      ?.intervalDurationStart?.duration
                      ? errors.data?.configuration?.staticInterval
                          ?.intervalDurationStart?.duration
                      : undefined
                  }
                />
              </Form.Group>
              <Form.Group as={Col} lg={3}>
                <Input
                  label={'Unité'}
                  register={form.register(
                    'data.configuration.staticInterval.intervalDurationStart.unit',
                    {
                      shouldUnregister: true,
                    }
                  )}
                  type={'select'}
                  options={[
                    {
                      value: 'NANOSECONDS',
                      text: 'Nanosecondes',
                    },
                    {
                      value: 'MICROSECONDS',
                      text: 'Microsecondes',
                    },
                    {
                      value: 'MILLISECONDS',
                      text: 'Millisecondes',
                    },
                    {
                      value: 'SECONDS',
                      text: 'Secondes',
                    },
                    {
                      value: 'MINUTES',
                      text: 'Minutes',
                    },
                    {
                      value: 'HOURS',
                      text: 'Heures',
                    },
                    {
                      value: 'DAYS',
                      text: 'Jours',
                    },
                    {
                      value: 'WEEKS',
                      text: 'Semaines',
                    },
                  ]}
                />
              </Form.Group>
              <Form.Group as={Col} lg={3}>
                <Input
                  type={'numeric'}
                  register={register(
                    'data.configuration.staticInterval.intervalDurationEnd.duration',
                    {
                      required: 'La duréé de fin est obligatoire',
                      shouldUnregister: true,
                    }
                  )}
                  label={'Intervale de fin'}
                  error={
                    errors.data?.configuration?.staticInterval
                      ?.intervalDurationStart?.duration
                      ? errors.data?.configuration?.staticInterval
                          ?.intervalDurationStart?.duration
                      : undefined
                  }
                />
              </Form.Group>
              <Form.Group as={Col} lg={3}>
                <Input
                  label={'Unité'}
                  register={form.register(
                    'data.configuration.staticInterval.intervalDurationEnd.unit',
                    {
                      shouldUnregister: true,
                    }
                  )}
                  type={'select'}
                  options={[
                    {
                      value: 'NANOSECONDS',
                      text: 'Nanosecondes',
                    },
                    {
                      value: 'MICROSECONDS',
                      text: 'Microsecondes',
                    },
                    {
                      value: 'MILLISECONDS',
                      text: 'Millisecondes',
                    },
                    {
                      value: 'SECONDS',
                      text: 'Secondes',
                    },
                    {
                      value: 'MINUTES',
                      text: 'Minutes',
                    },
                    {
                      value: 'HOURS',
                      text: 'Heures',
                    },
                    {
                      value: 'DAYS',
                      text: 'Jours',
                    },
                    {
                      value: 'WEEKS',
                      text: 'Semaines',
                    },
                  ]}
                />
              </Form.Group>
            </Row>
          </div>
        )}
        {configuration?.intervalType === 'DYNAMIC' && (
          <div>
            <Row>
              <Form.Group as={Col} lg={6} md={12}>
                <Input
                  register={register(
                    'data.configuration.dynamicInterval.intervalStartPattern',
                    {
                      required: 'Obligatoire',
                      shouldUnregister: true,
                    }
                  )}
                  label={'Start Pattern'}
                  error={
                    errors.data?.configuration?.dynamicInterval
                      ?.intervalStartPattern
                      ? errors.data?.configuration?.dynamicInterval
                          ?.intervalStartPattern
                      : undefined
                  }
                />
              </Form.Group>
              <Form.Group as={Col} lg={6} md={12}>
                <Input
                  register={register(
                    'data.configuration.dynamicInterval.intervalEndPattern',
                    {
                      required: 'Obligatoire',
                      shouldUnregister: true,
                    }
                  )}
                  label={'End Pattern'}
                  error={
                    errors.data?.configuration?.dynamicInterval
                      ?.intervalEndPattern
                      ? errors.data?.configuration?.dynamicInterval
                          ?.intervalEndPattern
                      : undefined
                  }
                />
              </Form.Group>
            </Row>
          </div>
        )}
        <Row>
          <Form.Group as={Col} lg={3}>
            <Input
              label={'Aggrégation'}
              register={form.register('data.configuration.aggregation')}
              type={'select'}
              options={[
                {
                  value: 'NONE',
                  text: 'Aucune',
                },
              ]}
            />
          </Form.Group>
        </Row>
      </Form>
    </FormProvider>
  )
}

export default GetOriginTelemetryForm
